<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore();

const emit = defineEmits(['transferred']);

const props = defineProps({
    leadId: {
        type: [Number, String],
        required: true,
    },
});

const availableOwners = ref([]);
const selectedOwnerId = ref('');
const currentOwnerId = ref('');
const loading = ref(false);
const saving = ref(false);

const ownerOptions = computed(() => {
    return availableOwners.value.map((owner) => ({
        value: String(owner.id),
        label: `${owner.name} - ${owner.email}`,
    }));
});

const selectedOwner = computed(() => {
    return availableOwners.value.find((owner) => String(owner.id) === selectedOwnerId.value) ?? null;
});

const hasSelectedNewOwner = computed(() => {
    return selectedOwnerId.value !== '' && selectedOwnerId.value !== currentOwnerId.value;
});

const loadOwners = async () => {
    if (!props.leadId) {
        availableOwners.value = [];
        return;
    }

    loading.value = true;

    try {
        const response = await axios.get(route('bo.api.v1.crm.lead.available-owners', props.leadId));
        availableOwners.value = response.data.team_members ?? [];
        currentOwnerId.value = response.data.current_owner_id ? String(response.data.current_owner_id) : '';
        selectedOwnerId.value = currentOwnerId.value;
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:moveContact.title'),
            message: error?.response?.data?.message || 'Failed to load available owners.',
            dismissAfter: 5000,
        });
    } finally {
        loading.value = false;
    }
};

const transferOwnership = async () => {
    if (!props.leadId || !hasSelectedNewOwner.value || saving.value) {
        return;
    }

    saving.value = true;

    try {
        await axios.post(route('bo.api.v1.crm.lead.transfer-ownership', props.leadId), {
            salesperson: selectedOwnerId.value,
        });

        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:updateContact'),
            message: selectedOwner.value ? `${selectedOwner.value.name} (${selectedOwner.value.email})` : '',
            dismissAfter: 3000,
        });

        emit('transferred', selectedOwner.value);
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:updateContact'),
            message: error?.response?.data?.message || 'Failed to update contact owner.',
            dismissAfter: 5000,
        });
    } finally {
        saving.value = false;
    }
};

watch(
    () => props.leadId,
    (nextLeadId, previousLeadId) => {
        if (nextLeadId && nextLeadId !== previousLeadId) {
            loadOwners();
        }
    }
);

onMounted(() => {
    loadOwners();
});
</script>

<template>
    <h3 class="font-semibold">{{ _mft('crm:moveContact.title') }}</h3>
    <div>
        <div
            v-if="loading"
            class="flex justify-center py-4"
        >
            <mf-spinner-medium class="w-12 h-6" />
        </div>

        <form
            v-else
            @submit.prevent="transferOwnership"
        >
            <input-select
                v-model:content="selectedOwnerId"
                :label="_mft('crm:contact.fields.salesperson')"
                :options="ownerOptions"
                name="salesperson"
            />

            <div class="flex justify-end mt-4">
                <button-success
                    type="submit"
                    :disabled="!hasSelectedNewOwner || saving"
                >
                    {{ _mft('crm:moveContact.title') }}
                </button-success>
            </div>
        </form>
    </div>
</template>
